import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import {
  ArrowLeft, Loader2, RefreshCw, Search, Send, CheckCircle2,
  MessageSquare, BookOpen, Plus, Trash2, Save, X, Paperclip,
  ChevronDown, ChevronUp, Sparkles, Filter, Eye, Layers, ShieldCheck
} from 'lucide-react'

// ─── Types ──────────────────────────────────────────────────────────────────
interface Thread {
  id: string
  user_id: string
  status: 'open' | 'answered' | 'resolved'
  context: Record<string, any>
  escalated: boolean
  escalated_at: string | null
  created_at: string
  last_message_at: string
  users?: { email: string; name: string | null } | null
  last_message_preview?: string
}

interface Msg {
  id: string
  thread_id: string
  sender: 'user' | 'ai' | 'admin'
  content: string
  attachment_url: string | null
  created_at: string
}

interface SupportDoc {
  id: string
  title: string
  content: string
  category: string | null
  priority: number
  is_active: boolean
  created_at: string
}

const call = async (action: string, extra: Record<string, any> = {}) => {
  const { data, error } = await supabase.functions.invoke('support', { body: { action, ...extra } })
  if (error) throw error
  return data
}

const fmtTime = (iso: string) => new Date(iso).toLocaleString(undefined, {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
})

// Category metadata: labels, icons, and theme accents
const CATEGORY_META: Record<string, { label: string; icon: string; color: string; badgeBg: string }> = {
  platform: { label: 'Platform & White-Label', icon: '🏢', color: '#38BDF8', badgeBg: 'rgba(56, 189, 248, 0.15)' },
  billing: { label: 'Plans & Billing', icon: '💳', color: '#4ADE80', badgeBg: 'rgba(74, 222, 128, 0.15)' },
  account: { label: 'Accounts & Sign-In', icon: '🔑', color: '#818CF8', badgeBg: 'rgba(129, 140, 248, 0.15)' },
  navigation: { label: 'Dashboards & Navigation', icon: '🧭', color: '#60A5FA', badgeBg: 'rgba(96, 165, 250, 0.15)' },
  homeschool: { label: 'Homeschool & Families', icon: '🏡', color: '#F472B6', badgeBg: 'rgba(244, 114, 182, 0.15)' },
  education: { label: 'Teaching & Mastery', icon: '🎓', color: '#A78BFA', badgeBg: 'rgba(167, 139, 250, 0.15)' },
  records: { label: 'Transcripts & Records', icon: '📜', color: '#FBBF24', badgeBg: 'rgba(251, 191, 36, 0.15)' },
  credentials: { label: 'Certificates & Credentials', icon: '🏆', color: '#E8C268', badgeBg: 'rgba(232, 194, 104, 0.15)' },
  affiliate: { label: 'Affiliate Program', icon: '🤝', color: '#2DD4BF', badgeBg: 'rgba(45, 212, 191, 0.15)' },
  sponsor: { label: 'Sponsorships', icon: '💚', color: '#34D399', badgeBg: 'rgba(52, 211, 153, 0.15)' },
  church: { label: 'Church Hubs', icon: '⛪', color: '#C084FC', badgeBg: 'rgba(192, 132, 252, 0.15)' },
  university: { label: 'University & Degrees', icon: '🏛️', color: '#6366F1', badgeBg: 'rgba(99, 102, 241, 0.15)' },
  'getting-started': { label: 'Getting Started', icon: '🚀', color: '#22D3EE', badgeBg: 'rgba(34, 211, 238, 0.15)' },
  troubleshooting: { label: 'Troubleshooting & Fixes', icon: '🛠️', color: '#F87171', badgeBg: 'rgba(248, 113, 113, 0.15)' },
}

const getCategoryMeta = (catKey: string | null) => {
  if (!catKey) return { label: 'General / Uncategorised', icon: '📁', color: '#9AA7BC', badgeBg: 'rgba(154, 167, 188, 0.12)' }
  const key = catKey.toLowerCase().trim()
  return CATEGORY_META[key] || {
    label: key.charAt(0).toUpperCase() + key.slice(1),
    icon: '📂',
    color: '#0EA5E9',
    badgeBg: 'rgba(14, 165, 233, 0.12)'
  }
}

function StatusBadge({ status, escalated }: { status: string; escalated?: boolean }) {
  const map: Record<string, { bg: string; color: string; label: string }> = {
    open: { bg: 'rgba(99, 102, 241, 0.18)', color: '#A5B4FC', label: 'Open' },
    answered: { bg: 'rgba(74, 222, 128, 0.18)', color: '#86EFAC', label: 'Answered' },
    resolved: { bg: 'rgba(148, 163, 184, 0.18)', color: '#CBD5E1', label: 'Resolved' },
  }
  const c = map[status] || map.open
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '3px 10px', borderRadius: 20, background: c.bg, color: c.color, border: '1px solid rgba(255,255,255,0.06)' }}>
        {c.label}
      </span>
      {escalated && (
        <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '3px 10px', borderRadius: 20, background: 'rgba(239, 68, 68, 0.18)', color: '#FCA5A5', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
          🆘 Human requested
        </span>
      )}
    </span>
  )
}

// ─── Thread detail (admin reads transcript + replies) ──────────────────────
function ThreadDetailView({ threadId, onBack }: { threadId: string; onBack: () => void }) {
  const [thread, setThread] = useState<Thread | null>(null)
  const [messages, setMessages] = useState<Msg[]>([])
  const [loading, setLoading] = useState(true)
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({})

  const load = useCallback(async () => {
    const data = await call('admin_get_thread', { thread_id: threadId })
    setThread(data.thread)
    setMessages(data.messages || [])
    setLoading(false)

    const attachments = (data.messages || []).filter((m: Msg) => m.attachment_url)
    for (const m of attachments) {
      const { data: signed } = await supabase.storage
        .from('support-attachments')
        .createSignedUrl(m.attachment_url!, 60 * 60)
      if (signed?.signedUrl) {
        setSignedUrls(prev => ({ ...prev, [m.attachment_url!]: signed.signedUrl }))
      }
    }
  }, [threadId])

  useEffect(() => { load() }, [load])

  const sendReply = async () => {
    if (!reply.trim()) return
    setSending(true)
    try {
      await call('admin_reply', { thread_id: threadId, content: reply.trim() })
      setReply('')
      await load()
    } finally {
      setSending(false)
    }
  }

  const resolve = async () => { await call('admin_resolve', { thread_id: threadId }); await load() }
  const reopen = async () => { await call('admin_reopen', { thread_id: threadId }); await load() }

  if (loading || !thread) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#0EA5E9' }} />
    </div>
  )

  const ctx = thread.context || {}

  return (
    <div>
      <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#9AA7BC', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 20, fontSize: 14 }}>
        <ArrowLeft size={15} /> Back to threads
      </button>

      {/* Header */}
      <div style={{ background: '#0F1930', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 18, padding: '24px 24px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 900, color: '#F2F5FA', margin: '0 0 6px' }}>
            {thread.users?.name || thread.users?.email || ctx.email || thread.user_id}
          </h2>
          <p style={{ fontSize: 13, color: '#9AA7BC', margin: '0 0 10px' }}>{thread.users?.email || ctx.email || '—'}</p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: '#9AA7BC' }}>
            {ctx.url && <span>📍 {ctx.url}</span>}
            {ctx.plan && <span>💳 {ctx.plan}</span>}
            {ctx.facility_id && <span>🏢 {ctx.facility_id}</span>}
          </div>
          <div style={{ marginTop: 10 }}><StatusBadge status={thread.status} escalated={thread.escalated} /></div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {thread.status !== 'resolved' ? (
            <button onClick={resolve} style={{ padding: '9px 18px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <CheckCircle2 size={14} /> Mark Resolved
            </button>
          ) : (
            <button onClick={reopen} style={{ padding: '9px 18px', background: 'rgba(255,255,255,0.08)', color: '#F2F5FA', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              Reopen
            </button>
          )}
        </div>
      </div>

      {/* Transcript */}
      <div style={{ background: '#0A0F1E', borderRadius: 16, border: '1px solid rgba(255,255,255,0.10)', padding: 20, marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 14, maxHeight: 480, overflowY: 'auto' }}>
        {messages.map(m => {
          const isAdmin = m.sender === 'admin'
          const isAi = m.sender === 'ai'
          const align = m.sender === 'user' ? 'flex-start' : 'flex-end'
          const bg = isAdmin ? 'rgba(232, 194, 104, 0.15)' : isAi ? '#131F38' : 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)'
          const color = isAdmin ? '#FDF4DC' : '#F2F5FA'
          const border = isAdmin ? '1px solid rgba(232, 194, 104, 0.35)' : isAi ? '1px solid rgba(255,255,255,0.08)' : 'none'

          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: align }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#9AA7BC', textTransform: 'uppercase', marginBottom: 4 }}>
                {isAdmin ? 'You (admin)' : isAi ? 'Still Waters AI' : 'User'} · {fmtTime(m.created_at)}
              </div>
              <div style={{ background: bg, color, border, padding: '10px 14px', borderRadius: 14, maxWidth: '75%', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {m.content}
                {m.attachment_url && (
                  <div style={{ marginTop: 8 }}>
                    {signedUrls[m.attachment_url] ? (
                      <img src={signedUrls[m.attachment_url]} alt="attachment" style={{ maxWidth: '100%', borderRadius: 8, display: 'block', border: '1px solid rgba(255,255,255,0.1)' }} />
                    ) : (
                      <span style={{ fontSize: 12, opacity: 0.7 }}><Paperclip size={12} style={{ display: 'inline' }} /> loading attachment…</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Reply box */}
      <div style={{ display: 'flex', gap: 10 }}>
        <textarea
          value={reply}
          onChange={e => setReply(e.target.value)}
          placeholder="Write a reply…"
          rows={3}
          style={{ flex: 1, padding: '12px 14px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.12)', background: '#0F1930', color: '#F2F5FA', fontSize: 14, resize: 'vertical', fontFamily: 'inherit', outline: 'none' }}
        />
        <button
          onClick={sendReply}
          disabled={sending || !reply.trim()}
          style={{ padding: '0 22px', background: 'linear-gradient(135deg, #0EA5E9, #0284C7)', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 13, cursor: sending ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6, opacity: reply.trim() ? 1 : 0.5 }}
        >
          {sending ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />} Send
        </button>
      </div>
    </div>
  )
}

// ─── Threads list ───────────────────────────────────────────────────────────
function ThreadsList() {
  const [threads, setThreads] = useState<Thread[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'open' | 'answered' | 'resolved'>('all')
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await call('admin_list_threads', filter === 'all' ? {} : { status: filter })
    setThreads(data.threads || [])
    setLoading(false)
  }, [filter])

  useEffect(() => { load() }, [load])

  if (selectedId) {
    return <ThreadDetailView threadId={selectedId} onBack={() => { setSelectedId(null); load() }} />
  }

  const filtered = threads.filter(t => {
    if (!search) return true
    const s = search.toLowerCase()
    return (t.users?.email || '').toLowerCase().includes(s) || (t.last_message_preview || '').toLowerCase().includes(s)
  })

  const openCount = threads.filter(t => t.status === 'open').length
  const escalatedCount = threads.filter(t => t.escalated && t.status !== 'resolved').length

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
        {[
          { label: 'Total Threads', value: threads.length, color: '#38BDF8' },
          { label: 'Open', value: openCount, color: '#FBBF24' },
          { label: 'Human Requested', value: escalatedCount, color: '#F87171' },
        ].map(s => (
          <div key={s.label} style={{ background: '#0F1930', borderRadius: 14, padding: '16px 18px', border: '1px solid rgba(255,255,255,0.08)', textAlign: 'center' }}>
            <div style={{ fontSize: 26, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA7BC', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter and Search Bar */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9AA7BC' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by email or message…"
            style={{ width: '100%', height: 40, paddingLeft: 38, paddingRight: 12, borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)', background: '#0A0F1E', color: '#F2F5FA', fontSize: 13.5, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {(['all', 'open', 'answered', 'resolved'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '7px 14px',
                borderRadius: 9,
                border: filter === f ? '1px solid rgba(14, 165, 233, 0.4)' : '1px solid rgba(255,255,255,0.08)',
                background: filter === f ? '#0EA5E9' : '#0F1930',
                color: filter === f ? '#fff' : '#9AA7BC',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
                textTransform: 'capitalize',
                transition: 'all 0.15s ease'
              }}
            >
              {f}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#0F1930', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#F2F5FA' }}
        >
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /> Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: '#0EA5E9' }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', background: '#0F1930', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)' }}>
          <MessageSquare size={36} style={{ opacity: 0.25, display: 'block', margin: '0 auto 12px', color: '#9AA7BC' }} />
          <p style={{ color: '#9AA7BC', margin: 0 }}>No support threads matching this filter.</p>
        </div>
      ) : (
        <div style={{ background: '#0F1930', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0A0F1E', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  {['User', 'Last Message', 'Status', 'Updated', ''].map((h, i) => (
                    <th key={i} style={{ padding: '12px 18px', textAlign: 'left', fontSize: 11, fontWeight: 800, color: '#9AA7BC', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr
                    key={t.id}
                    style={{ borderTop: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', transition: 'background 0.15s ease' }}
                    onClick={() => setSelectedId(t.id)}
                    className="hover:bg-white/[0.03]"
                  >
                    <td style={{ padding: '14px 18px' }}>
                      <div style={{ fontWeight: 700, color: '#F2F5FA', fontSize: 14 }}>{t.users?.name || t.users?.email || t.context?.email || 'Unknown user'}</div>
                      <div style={{ fontSize: 12, color: '#9AA7BC' }}>{t.users?.email || t.context?.email || ''}</div>
                    </td>
                    <td style={{ padding: '14px 18px', fontSize: 13, color: '#CBD5E1', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.last_message_preview || '—'}
                    </td>
                    <td style={{ padding: '14px 18px' }}><StatusBadge status={t.status} escalated={t.escalated} /></td>
                    <td style={{ padding: '14px 18px', fontSize: 12, color: '#9AA7BC', whiteSpace: 'nowrap' }}>{fmtTime(t.last_message_at)}</td>
                    <td style={{ padding: '14px 18px', color: '#0EA5E9', fontWeight: 800 }}>→</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Knowledge docs manager: CATEGORY-ORGANIZED ─────────────────────────────
function DocsManager() {
  const [docs, setDocs] = useState<SupportDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<SupportDoc | null>(null)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({ title: '', content: '', category: '', priority: 7 })
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [expandedDocIds, setExpandedDocIds] = useState<Set<string>>(new Set())
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set())

  const load = useCallback(async () => {
    setLoading(true)
    const data = await call('admin_list_docs')
    setDocs(data.docs || [])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const startCreate = () => {
    setForm({ title: '', content: '', category: activeCategory !== 'all' ? activeCategory : '', priority: 7 })
    setCreating(true)
    setEditing(null)
  }

  const startEdit = (d: SupportDoc) => {
    setForm({ title: d.title, content: d.content, category: d.category || '', priority: d.priority })
    setEditing(d)
    setCreating(false)
  }

  const cancel = () => { setCreating(false); setEditing(null) }

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) return
    setSaving(true)
    try {
      if (editing) {
        await call('admin_update_doc', { id: editing.id, ...form, priority: Number(form.priority) })
      } else {
        await call('admin_create_doc', { ...form, priority: Number(form.priority) })
      }
      cancel()
      await load()
    } finally {
      setSaving(false)
    }
  }

  const toggleActive = async (d: SupportDoc) => {
    await call('admin_update_doc', { id: d.id, is_active: !d.is_active })
    await load()
  }

  const remove = async (d: SupportDoc) => {
    if (!confirm(`Delete "${d.title}"? This removes it from the AI's knowledge immediately.`)) return
    await call('admin_delete_doc', { id: d.id })
    await load()
  }

  const toggleDocExpand = (id: string) => {
    setExpandedDocIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleCategoryCollapse = (cat: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }

  // Filter docs by search and active category
  const filteredDocs = useMemo(() => {
    return docs.filter(d => {
      const matchesCategory = activeCategory === 'all' || (d.category?.toLowerCase() || '') === activeCategory.toLowerCase()
      if (!matchesCategory) return false

      if (!search.trim()) return true
      const q = search.toLowerCase()
      return (
        d.title.toLowerCase().includes(q) ||
        (d.category || '').toLowerCase().includes(q) ||
        d.content.toLowerCase().includes(q)
      )
    })
  }, [docs, activeCategory, search])

  // Group filtered docs by category
  const groupedDocs = useMemo(() => {
    const groups: Record<string, SupportDoc[]> = {}
    for (const d of filteredDocs) {
      const cat = (d.category || 'general').toLowerCase()
      if (!groups[cat]) groups[cat] = []
      groups[cat].push(d)
    }
    // Sort docs in each category by priority descending, then title
    for (const cat in groups) {
      groups[cat].sort((a, b) => b.priority - a.priority || a.title.localeCompare(b.title))
    }
    return groups
  }, [filteredDocs])

  // Category counts from ALL docs (for the pills badge)
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const d of docs) {
      const cat = (d.category || 'general').toLowerCase()
      counts[cat] = (counts[cat] || 0) + 1
    }
    return counts
  }, [docs])

  const categoryKeys = useMemo(() => {
    const keys = Object.keys(categoryCounts)
    // Put platform, billing, homeschool, church, credentials near the front
    const priorityOrder = ['platform', 'billing', 'homeschool', 'education', 'credentials', 'church', 'affiliate', 'sponsor', 'records', 'navigation', 'account', 'getting-started', 'troubleshooting']
    return keys.sort((a, b) => {
      const idxA = priorityOrder.indexOf(a)
      const idxB = priorityOrder.indexOf(b)
      if (idxA !== -1 && idxB !== -1) return idxA - idxB
      if (idxA !== -1) return -1
      if (idxB !== -1) return 1
      return a.localeCompare(b)
    })
  }, [categoryCounts])

  const isFormOpen = creating || !!editing

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header controls & stats */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 14 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#38BDF8', background: 'rgba(56, 189, 248, 0.12)', padding: '3px 10px', borderRadius: 20 }}>
              {docs.length} Live Topics
            </span>
            <span style={{ fontSize: 12, color: '#9AA7BC' }}>
              Structured knowledge for Still Waters AI Support
            </span>
          </div>
          <p style={{ fontSize: 13, color: '#9AA7BC', margin: '6px 0 0', maxWidth: 640, lineHeight: 1.5 }}>
            Organised by product category. Highest priority items (e.g. Priority 10) are injected into the AI context first when token budgets are tight.
          </p>
        </div>

        <button
          onClick={startCreate}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '10px 20px',
            background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: 12,
            fontWeight: 800,
            fontSize: 13.5,
            cursor: 'pointer',
            flexShrink: 0,
            boxShadow: '0 4px 16px rgba(14, 165, 233, 0.35)',
            transition: 'all 0.15s ease'
          }}
        >
          <Plus size={16} /> Add Topic
        </button>
      </div>

      {/* Category Filter Pills (no more wall of text!) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#9AA7BC', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          <Filter size={13} color="#0EA5E9" />
          Filter by Category:
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => setActiveCategory('all')}
            style={{
              padding: '7px 14px',
              borderRadius: 10,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: activeCategory === 'all' ? '#0EA5E9' : '#0F1930',
              color: activeCategory === 'all' ? '#FFFFFF' : '#CBD5E1',
              border: activeCategory === 'all' ? '1px solid #38BDF8' : '1px solid rgba(255,255,255,0.08)',
              boxShadow: activeCategory === 'all' ? '0 4px 14px rgba(14, 165, 233, 0.3)' : 'none',
              transition: 'all 0.15s ease'
            }}
          >
            <Layers size={13} />
            <span>All Categories</span>
            <span style={{ fontSize: 10.5, padding: '1px 6px', borderRadius: 10, background: activeCategory === 'all' ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)', fontWeight: 800 }}>
              {docs.length}
            </span>
          </button>

          {categoryKeys.map(catKey => {
            const meta = getCategoryMeta(catKey)
            const count = categoryCounts[catKey] || 0
            const isSelected = activeCategory === catKey

            return (
              <button
                key={catKey}
                onClick={() => setActiveCategory(catKey)}
                style={{
                  padding: '7px 13px',
                  borderRadius: 10,
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: isSelected ? meta.color : '#0F1930',
                  color: isSelected ? '#FFFFFF' : '#CBD5E1',
                  border: isSelected ? `1px solid ${meta.color}` : '1px solid rgba(255,255,255,0.08)',
                  boxShadow: isSelected ? `0 4px 14px ${meta.color}40` : 'none',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>{meta.icon}</span>
                <span>{meta.label}</span>
                <span style={{ fontSize: 10.5, padding: '1px 6px', borderRadius: 10, background: isSelected ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.08)', color: isSelected ? '#fff' : meta.color, fontWeight: 800 }}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Search Input Bar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search size={15} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9AA7BC' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search docs by title, keyword, or content snippet…"
            style={{
              width: '100%',
              height: 42,
              paddingLeft: 40,
              paddingRight: search ? 36 : 14,
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.12)',
              background: '#0A0F1E',
              color: '#F2F5FA',
              fontSize: 13.5,
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#9AA7BC', cursor: 'pointer' }}
            >
              <X size={15} />
            </button>
          )}
        </div>

        {activeCategory !== 'all' && (
          <button
            onClick={() => setActiveCategory('all')}
            style={{
              padding: '9px 14px',
              borderRadius: 10,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#9AA7BC',
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Clear category filter
          </button>
        )}
      </div>

      {/* Create / Edit Form Drawer */}
      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -14 }}
            style={{
              background: '#0F1930',
              borderRadius: 16,
              padding: 22,
              border: '1px solid rgba(14, 165, 233, 0.35)',
              boxShadow: '0 12px 36px rgba(0,0,0,0.4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 14
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(14, 165, 233, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0EA5E9' }}>
                  <Sparkles size={16} />
                </div>
                <h4 style={{ margin: 0, fontWeight: 800, fontSize: 16, color: '#F2F5FA' }}>
                  {editing ? 'Edit Knowledge Doc' : 'Add New Support Topic'}
                </h4>
              </div>
              <button onClick={cancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9AA7BC' }}>
                <X size={18} />
              </button>
            </div>

            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Title (e.g. 'How church seats work')"
              style={{
                height: 44,
                padding: '0 14px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.14)',
                background: '#0A0F1E',
                color: '#F2F5FA',
                fontSize: 14,
                outline: 'none'
              }}
            />

            <textarea
              value={form.content}
              onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
              placeholder="Write the exact answer knowledge. Use clear, factual steps. Remember drift law: do NOT hardcode dollar pricing (the AI edge function injects live pricing from database automatically)."
              rows={8}
              style={{
                padding: '12px 14px',
                borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.14)',
                background: '#0A0F1E',
                color: '#F2F5FA',
                fontSize: 13.5,
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                lineHeight: 1.55
              }}
            />

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#9AA7BC', textTransform: 'uppercase' }}>
                  Category
                </label>
                <input
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  placeholder="e.g. platform, billing, church, homeschool, credentials"
                  style={{
                    height: 40,
                    padding: '0 12px',
                    borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.14)',
                    background: '#0A0F1E',
                    color: '#F2F5FA',
                    fontSize: 13,
                    outline: 'none'
                  }}
                />
              </div>

              <div style={{ width: 140, display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#9AA7BC', textTransform: 'uppercase' }}>
                  Priority (0-10)
                </label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={form.priority}
                  onChange={e => setForm(f => ({ ...f, priority: Number(e.target.value) }))}
                  style={{
                    height: 40,
                    padding: '0 12px',
                    borderRadius: 10,
                    border: '1px solid rgba(255,255,255,0.14)',
                    background: '#0A0F1E',
                    color: '#F2F5FA',
                    fontSize: 13,
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <button
                onClick={save}
                disabled={saving || !form.title.trim() || !form.content.trim()}
                style={{
                  padding: '11px 24px',
                  background: 'linear-gradient(135deg, #0EA5E9, #0284C7)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontWeight: 800,
                  fontSize: 13.5,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  opacity: saving || !form.title.trim() || !form.content.trim() ? 0.5 : 1
                }}
              >
                {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
                {editing ? 'Save Changes' : 'Publish to AI Knowledge'}
              </button>

              <button
                onClick={cancel}
                style={{
                  padding: '11px 18px',
                  background: 'transparent',
                  color: '#9AA7BC',
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content Area: Grouped Categories */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 56 }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#0EA5E9' }} />
        </div>
      ) : Object.keys(groupedDocs).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '56px 24px', background: '#0F1930', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)' }}>
          <BookOpen size={36} style={{ opacity: 0.25, display: 'block', margin: '0 auto 12px', color: '#9AA7BC' }} />
          <p style={{ color: '#F2F5FA', fontWeight: 700, fontSize: 15, margin: '0 0 4px' }}>No matching knowledge topics</p>
          <p style={{ color: '#9AA7BC', fontSize: 13, margin: 0 }}>Try clearing your search or category filter.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {Object.entries(groupedDocs).map(([catKey, catDocs]) => {
            const meta = getCategoryMeta(catKey)
            const isCollapsed = collapsedCategories.has(catKey)

            return (
              <div
                key={catKey}
                style={{
                  background: '#0F1930',
                  borderRadius: 18,
                  border: '1px solid rgba(255,255,255,0.08)',
                  overflow: 'hidden',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
                }}
              >
                {/* Category Header Banner */}
                <div
                  onClick={() => toggleCategoryCollapse(catKey)}
                  style={{
                    padding: '14px 18px',
                    background: 'rgba(255,255,255,0.02)',
                    borderBottom: isCollapsed ? 'none' : '1px solid rgba(255,255,255,0.06)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 18 }}>{meta.icon}</span>
                    <span style={{ fontWeight: 800, fontSize: 15, color: '#F2F5FA' }}>
                      {meta.label}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: meta.color,
                        background: meta.badgeBg,
                        padding: '2px 9px',
                        borderRadius: 14,
                        border: `1px solid ${meta.color}35`
                      }}
                    >
                      {catDocs.length} {catDocs.length === 1 ? 'doc' : 'docs'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#9AA7BC', fontSize: 12 }}>
                    <span>{isCollapsed ? 'Show' : 'Hide'}</span>
                    {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                  </div>
                </div>

                {/* Category Doc Cards */}
                {!isCollapsed && (
                  <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {catDocs.map(d => {
                      const isExpanded = expandedDocIds.has(d.id)

                      return (
                        <div
                          key={d.id}
                          style={{
                            background: d.is_active ? '#0A0F1E' : 'rgba(10, 15, 30, 0.45)',
                            borderRadius: 14,
                            border: d.is_active ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.04)',
                            padding: '14px 16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            opacity: d.is_active ? 1 : 0.65,
                            transition: 'all 0.15s ease'
                          }}
                        >
                          {/* Row 1: Title, Priority, Active & Actions */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flex: 1 }}>
                              <span style={{ fontWeight: 800, color: '#F2F5FA', fontSize: 14 }}>
                                {d.title}
                              </span>

                              {/* Priority Badge */}
                              <span
                                style={{
                                  fontSize: 10.5,
                                  fontWeight: 800,
                                  color: d.priority >= 10 ? '#FBBF24' : d.priority >= 7 ? '#38BDF8' : '#9AA7BC',
                                  background: d.priority >= 10 ? 'rgba(251, 191, 36, 0.12)' : 'rgba(255,255,255,0.06)',
                                  border: d.priority >= 10 ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid rgba(255,255,255,0.08)',
                                  padding: '2px 8px',
                                  borderRadius: 14
                                }}
                              >
                                {d.priority >= 10 ? '⭐ Priority 10' : `Priority ${d.priority}`}
                              </span>
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                              <button
                                onClick={() => toggleActive(d)}
                                title={d.is_active ? 'Deactivate this topic' : 'Activate this topic'}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: 8,
                                  border: d.is_active ? '1px solid rgba(74, 222, 128, 0.35)' : '1px solid rgba(255,255,255,0.1)',
                                  background: d.is_active ? 'rgba(74, 222, 128, 0.12)' : 'rgba(255,255,255,0.05)',
                                  fontSize: 11.5,
                                  fontWeight: 800,
                                  cursor: 'pointer',
                                  color: d.is_active ? '#4ADE80' : '#9AA7BC',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 5
                                }}
                              >
                                {d.is_active ? <ShieldCheck size={13} /> : null}
                                {d.is_active ? 'Active' : 'Inactive'}
                              </button>

                              <button
                                onClick={() => startEdit(d)}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: 8,
                                  border: '1px solid rgba(255,255,255,0.12)',
                                  background: 'rgba(255,255,255,0.05)',
                                  fontSize: 11.5,
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  color: '#F2F5FA'
                                }}
                              >
                                Edit
                              </button>

                              <button
                                onClick={() => remove(d)}
                                style={{
                                  padding: '6px 9px',
                                  borderRadius: 8,
                                  border: '1px solid rgba(239, 68, 68, 0.25)',
                                  background: 'rgba(239, 68, 68, 0.12)',
                                  cursor: 'pointer',
                                  color: '#F87171',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                                title="Delete document"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>

                          {/* Row 2: Content Preview or Expanded Content */}
                          <div style={{ fontSize: 13, color: '#9AA7BC', lineHeight: 1.55 }}>
                            {isExpanded ? (
                              <div style={{ whiteSpace: 'pre-wrap', background: 'rgba(255,255,255,0.02)', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)', marginTop: 4 }}>
                                {d.content}
                              </div>
                            ) : (
                              <div>
                                {d.content.slice(0, 160)}{d.content.length > 160 ? '…' : ''}
                              </div>
                            )}
                          </div>

                          {/* Show more / show less toggle */}
                          {d.content.length > 160 && (
                            <button
                              onClick={() => toggleDocExpand(d.id)}
                              style={{
                                alignSelf: 'flex-start',
                                background: 'none',
                                border: 'none',
                                color: '#0EA5E9',
                                fontSize: 11.5,
                                fontWeight: 700,
                                cursor: 'pointer',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4
                              }}
                            >
                              <Eye size={12} />
                              {isExpanded ? 'Collapse text' : 'Read full answer knowledge'}
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── MAIN SUPPORT MANAGEMENT COMPONENT ──────────────────────────────────────
export function SupportManagement() {
  const [tab, setTab] = useState<'threads' | 'docs'>('docs') // Default to docs for easy knowledge management

  return (
    <div className="p-6 space-y-6">
      {/* Top Header */}
      <div>
        <h2 style={{ fontSize: 24, fontWeight: 900, color: '#F2F5FA', margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <MessageSquare size={24} color="#0EA5E9" />
          Support Centre
        </h2>
        <p style={{ color: '#9AA7BC', margin: 0, fontSize: 14 }}>
          AI-first help with human escalation, and the knowledge topics that power it
        </p>
      </div>

      {/* Tab Switcher */}
      <div
        style={{
          display: 'flex',
          gap: 6,
          background: '#0F1930',
          borderRadius: 14,
          padding: 5,
          border: '1px solid rgba(255,255,255,0.08)',
          alignSelf: 'flex-start',
          width: 'fit-content'
        }}
      >
        {([
          ['docs', 'Knowledge Topics', <BookOpen size={14} key="i2" />],
          ['threads', 'Support Threads', <MessageSquare size={14} key="i1" />]
        ] as const).map(([id, label, icon]) => (
          <button
            key={id}
            onClick={() => setTab(id as any)}
            style={{
              padding: '9px 18px',
              borderRadius: 10,
              border: tab === id ? '1px solid rgba(14, 165, 233, 0.4)' : 'none',
              background: tab === id ? '#0EA5E9' : 'transparent',
              color: tab === id ? '#FFFFFF' : '#9AA7BC',
              fontWeight: 800,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              boxShadow: tab === id ? '0 4px 12px rgba(14, 165, 233, 0.3)' : 'none',
              transition: 'all 0.15s ease'
            }}
          >
            {icon} {label}
          </button>
        ))}
      </div>

      {/* Active Tab View */}
      {tab === 'threads' ? <ThreadsList /> : <DocsManager />}
    </div>
  )
}